import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import {
  CellClickedEvent,
  ColDef,
  ColGroupDef,
  ColumnApi,
  ColumnState,
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  MenuItemDef
} from 'ag-grid-community';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { EntityService } from '@bo-client/shared/dynamic-search-serverside/entity-service';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, distinct, map, shareReplay, tap } from 'rxjs/operators';
import { Observe } from '@bo-client/shared/observe.util';
import { ThemePalette } from '@angular/material/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AG_GRID_LOCALE_DE } from '@ag-grid-community/locale';
import { PageConfigModel, PageConfigName } from '@bo-client/shared/models/userBackOfficeSettings';
import { UserConfigService } from '@bo-client/shared/services/user-config.service';
import { MatDialogSize } from '@bo-client/shared/constants/mat-dialog-size';
import { EntityListInfoModalComponent } from '@bo-client/shared/entity-list-page/entity-list-info-modal/entity-list-info-modal.component';

export type ColOrColGroupDef = (ColDef | ColGroupDef) & { hideColumnInExport?: boolean };

export interface CreatableAction {
  title: string;
  action: (onDataChangeCallback?: () => void) => void;
  description?: string;
  color?: ThemePalette;
  iconName?: string;
  isVisible?: boolean;
}

class BaseRowNode {}

// Todo: irgendwo hier in der Component geht was kaputt, wenn die datasource zu schnell lädt -> Die Liste lädt zwar, aber es werden keine Spalten angezeigt, auch nach refresh Button nicht (Beispiel Anschlussänderung THN)
@Component({
  selector: 'app-entity-list-page',
  templateUrl: './entity-list-page.component.html',
  styleUrls: ['./entity-list-page.component.scss']
})
export class EntityListPageComponent implements OnInit {
  matDialog = inject(MatDialog);

  @Input()
  entityPrefix: PageConfigName;

  @Observe('entityPrefix')
  entityPrefix$: Observable<PageConfigName>;

  @Input()
  defaultColumnDefs: ColOrColGroupDef[];

  @Observe('defaultColumnDefs')
  defaultColumnDefs$: Observable<ColOrColGroupDef[]>;

  columnDefs$: Observable<ColOrColGroupDef[]>;

  @Input()
  public entityService: EntityService;

  @Input()
  title: string;

  @Input()
  creatableActions: CreatableAction[] = null;

  /**
   * Factory that returns the absolute or relative URL for a given row.
   * If provided, the component will handle navigation for row clicks.
   */
  @Input()
  rowLinkFactory?: (row: BaseRowNode) => string | null | undefined;

  @Input()
  suppressNavigateForFields: string[] = [];

  @Output()
  cellClickedEvent: EventEmitter<CellClickedEvent> = new EventEmitter<CellClickedEvent>();

  @Output()
  openInNewTabEvent: EventEmitter<CellClickedEvent> = new EventEmitter<CellClickedEvent>();

  private pageConfig$: Observable<PageConfigModel>;
  private gridReady$: Subject<boolean> = new Subject<boolean>();

  private currentPage: number;
  private initialPaginationChanged = false;
  isExporting: boolean;

  private saveGridSettings = this.debounce(this.saveState);
  /**
   * const savedState = gridOptions.columnApi.getColumnState();
   *
   * // restore the column state
   * gridOptions.columnApi.applyColumnState({ state: savedState });
   */
  constructor(
    private dataShareService: DataShareService,
    private userConfigService: UserConfigService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.pageConfig$ = combineLatest([userConfigService.userConfigs$, this.entityPrefix$]).pipe(
      distinct(),
      map(([userConfiguration, entityPrefix]) => userConfiguration.pageConfigs[entityPrefix] || null),
      shareReplay(1)
    );

    this.columnDefs$ = this.defaultColumnDefs$;

    this.pageConfig$.pipe(debounceTime(500)).subscribe((config) => {
      if (this.gridColumnApi) {
        this.gridColumnApi.applyColumnState({
          state: config.columnState,
          applyOrder: true
        });
      }
      if (this.gridApi) {
        this.gridApi.setFilterModel(config.filterModel);
      }
    });
  }

  localeText = AG_GRID_LOCALE_DE;

  @Input()
  defaultPageSize = 20;

  private gridApi!: GridApi;

  // This is a hidden Ag-Grid that is used to export all pages as excel. Since AG-Grid only exports the rows that are loaded on the client
  // -> since we do server-side-row-model the export would only export the current page.
  exportGridApi!: GridApi;
  exportGridColumnApi: ColumnApi;
  showExportAgGrid: boolean;

  private gridColumnApi: ColumnApi;

  public dataSource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => {
      // This is a hack to remove the colId: "0" on initializing the table the first time since it throws off the backend
      function sanitizeColumnState(columnState: ColumnState[]) {
        if (columnState && columnState.length > 0) {
          return columnState.filter((col) => col.colId != '0');
        }
        return columnState;
      }

      this.entityService
        .loadData(
          this.dataShareService.currentTenant.id,
          'master',
          this.gridApi.paginationGetCurrentPage(),
          this.gridApi.paginationGetPageSize(),
          this.gridApi.getFilterModel(),
          sanitizeColumnState(this.gridColumnApi.getColumnState())
        )
        .subscribe({
          next: (response) => {
            params.success({ rowData: response.content, rowCount: response.totalElements });
          },
          error: (error) => {
            // eslint-disable-next-line no-console
            console.error('Fehler beim Laden der Daten:', error);
            params.fail();
          }
        });
    }
  };

  public dataExportSource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => {
      // This is a hack to remove the colId: "0" on initializing the table the first time since it throws off the backend
      function sanitizeColumnState(columnState: ColumnState[]) {
        if (columnState && columnState.length > 0) {
          return columnState.filter((col) => col.colId != '0');
        }
        return columnState;
      }

      this.entityService
        .loadData(
          this.dataShareService.currentTenant.id,
          'master',
          this.exportGridApi.paginationGetCurrentPage(),
          this.exportGridApi.paginationGetPageSize(),
          this.exportGridApi.getFilterModel(),
          sanitizeColumnState(this.exportGridColumnApi.getColumnState())
        )
        .subscribe({
          next: (response) => {
            params.success({ rowData: response.content, rowCount: response.totalElements });
          },
          error: (error) => {
            // eslint-disable-next-line no-console
            console.error('Fehler beim Laden der Daten:', error);
            params.fail();
          }
        });
    }
  };

  triggerExport() {
    this.exportGridApi.refreshServerSide();
  }

  // This will work only one time woopie
  onExportGridPaginationChanged($event: any) {
    // Ich weiß nicht, warum das einen doppelten Export verhindert, aber das tut es
    if ($event.keepRenderedRows === false) return;
    this.exportGridApi.exportDataAsExcel();
    this.isExporting = false;
  }

  // Todo refactor this
  onExportGridReady(params: GridReadyEvent) {
    this.exportGridApi = params.api;
    this.exportGridColumnApi = params.columnApi;
    this.exportGridApi.setServerSideDatasource(this.dataExportSource);
    this.exportGridApi.setFilterModel(this.gridApi.getFilterModel());
    this.exportGridApi.paginationSetPageSize(10000);
    this.exportGridColumnApi.applyColumnState({
      state: this.gridColumnApi.getColumnState(),
      applyOrder: true
    });

    // the first time we initialize the export grid we want to direvtyl trigger a refresh the next time
    // the refresh is triggered instead of loading the grid
    // hacky but works for now
    this.exportGridApi.refreshServerSide();
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.updateGridPage('onGridReady');
    this.gridColumnApi = params.columnApi;
    this.gridApi.setServerSideDatasource(this.dataSource);
    this.gridReady$.next(true);

    if (this.creatableActions && this.creatableActions.length > 0) {
      this.creatableActions = this.creatableActions.map((action) => ({
        ...action
      }));
    }

    this.gridApi.addEventListener('columnResized', () => {
      this.saveGridSettings();
    });
    this.gridApi.addEventListener('columnMoved', () => {
      this.saveGridSettings();
    });
    this.gridApi.addEventListener('columnPinned', () => {
      this.saveGridSettings();
    });
    this.gridApi.addEventListener('columnVisible', () => {
      this.saveGridSettings();
    });
    this.gridApi.addEventListener('gridColumnsChanged', () => {
      // this.saveGridSettings();
    });
    this.gridApi.addEventListener('filterChanged', () => {
      this.saveGridSettings();
    });
    this.gridApi.addEventListener('sortChanged', () => {
      this.saveGridSettings();
    });
    this.gridApi.addEventListener('paginationChanged', () => {
      if (!this.initialPaginationChanged) {
        this.initialPaginationChanged = true;
        this.gridApi.paginationGoToPage(this.currentPage);
        return;
      }
      this.updatePageQueryParams(this.gridApi.paginationGetCurrentPage());
    });
  }

  ngOnInit(): void {
    this.dataShareService.currentTenant$.subscribe((tenant) => {
      if (!this.route.snapshot.queryParams.page) {
        this.updatePageQueryParams(0);
      }
      this.route.queryParams.subscribe((params) => {
        this.currentPage = Number(params.page);
        this.updateGridPage('onInit');
      });
      if (this.gridApi) {
        this.gridApi.setServerSideDatasource(this.dataSource);
      }
    });

    combineLatest([this.pageConfig$, this.gridReady$])
      .pipe(
        map(([columnState, gridReady]) => columnState),
        tap((state) => this.restoreState(state))
      )
      .subscribe();
  }

  onCellClicked($event: CellClickedEvent) {
    if (
      this.rowLinkFactory &&
      !this.suppressNavigateForFields.includes($event.colDef?.field) &&
      $event.colDef?.field !== 'rowActions'
    ) {
      this.openRow($event.data, false);
    } else {
      this.cellClickedEvent.emit($event);
    }
  }

  private openRow(rowData: any, openInNewTab: boolean) {
    if (!this.rowLinkFactory) {
      return;
    }

    const url = this.rowLinkFactory(rowData);
    if (!url) {
      return;
    }

    if (openInNewTab) {
      window.open('#/' + url, '_blank');
      return;
    }

    this.router.navigateByUrl(url).then();
  }

  // Used by ag-grid as gridOption getContextMenuItems, see:
  // https://www.ag-grid.com/angular-data-grid/context-menu/
  getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    const itemsToRemove = ['cut', 'paste', 'separator', 'export'];
    const defaultItems = (params.defaultItems || []).filter((item) => !itemsToRemove.includes(item)) as (
      | string
      | MenuItemDef
    )[];

    // If no row link is configured or no row data, fall back to default menu
    if (!this.rowLinkFactory || !params.node?.data) {
      return defaultItems;
    }

    const url = this.rowLinkFactory(params.node.data as any);
    if (!url) {
      return defaultItems;
    }

    const openInNewTabItem: MenuItemDef = {
      name: 'In neuem Tab öffnen',
      action: () => this.openRow(params.node!.data, true)
    };

    return [openInNewTabItem, 'separator', ...defaultItems];
  };

  refresh() {
    this.gridApi.refreshServerSide();
  }

  resetFilters() {
    this.gridApi.setFilterModel(null);
  }

  resetColumnArrangement() {
    const mapColumnWidths = (colDefs: ColOrColGroupDef[]): { key: string; newWidth: number }[] => {
      const mappedColDefs = colDefs.map((colDef) => {
        if ('children' in colDef) {
          return mapColumnWidths(colDef.children);
        }
        return { key: colDef.field || colDef.colId, newWidth: 200 };
      });

      return (mappedColDefs as [])
        .reduce((acc, val) => acc.concat(val), [])
        .map((col) => ({
          key: col.key,
          newWidth: col.newWidth
        }));
    };

    this.gridColumnApi.resetColumnState();
    this.gridColumnApi.setColumnWidths(mapColumnWidths(this.defaultColumnDefs));
  }

  saveState() {
    const columnState = this.gridColumnApi.getColumnState();
    const filterModel = this.gridApi.getFilterModel();

    // reste

    localStorage.setItem('columnState', JSON.stringify(columnState));
    localStorage.setItem('filterModel', JSON.stringify(filterModel));

    this.userConfigService.pageConfig.update(
      {
        columnState: columnState,
        filterModel: filterModel
      },
      this.entityPrefix
    );
  }

  private restoreState(state: PageConfigModel) {
    if (state && state.columnState && this.gridColumnApi) {
      this.gridColumnApi.applyColumnState({ state: state.columnState, applyOrder: true });
    }
    if (state && state.filterModel && this.gridApi) {
      this.gridApi.setFilterModel(state.filterModel);
    }
  }

  private updatePageQueryParams(page: number) {
    if (this.currentPage === page) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page: page },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private updateGridPage(log: string) {
    if (!this.gridApi || !this.currentPage || this.currentPage === this.gridApi.paginationGetCurrentPage()) {
      return;
    }
    this.gridApi.paginationGoToPage(this.currentPage);
  }

  public exportToExcel() {
    this.isExporting = true;
    if (!this.showExportAgGrid) {
      this.showExportAgGrid = true;
    } else {
      this.triggerExport();
    }
  }

  openInfoModal() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'entity-list-info-modal';
    dialogConfig.maxHeight = MatDialogSize.maxHeight;
    dialogConfig.width = MatDialogSize.width;
    dialogConfig.autoFocus = false;
    dialogConfig.restoreFocus = false;

    this.matDialog.open(EntityListInfoModalComponent, dialogConfig);
  }

  private debounce(func: () => unknown, wait = 300) {
    let timeout: ReturnType<typeof setTimeout>;
    return function (...args: unknown[]) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}
